/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { HoverCard, HoverCardType } from '@fluentui/react/lib/HoverCard';
import { IPersonaProps, IPersonaSharedProps, PersonaCoin, PersonaInitialsColor } from '@fluentui/react/lib/Persona';
import {
    IBasePicker,
    IPeoplePickerItemSelectedProps,
    PeoplePickerItem,
    ValidationState,
} from '@fluentui/react/lib/Pickers';
import * as React from 'react';
import { ApprovalPersonaProps } from '../../../models/ApprovalPersonaProps';
import { getThemedIcon, IconName } from '../../../resources/icons/Icons';
import {
    getPeopleResultsForQueryInPersonalApp,
    getPeopleResultsForQueryInTeam,
    getUsersAndGroupsForQuery,
    validateEmail,
    validateRawText,
} from '../../../utilities/PeoplePickerUtils';
import { getPeopleResultsandTeamTagResultsForQueryInTeam } from '../../../utilities/TeamTagPeoplePickerUtils';
import { ApproverPeoplePickerComponent } from '../ApproverPeoplePicker/ApproverPeoplePicker.component';
import { TeamTagCard } from '../TeamTagCard';

// Different data source for template instance setup scenario
export enum DataSourceType {
    TenantWidePeople,
    TeamWidePeople,
    TenantWidePeopleAndGroup,
}

export interface ITemplatePeoplePickerProps {
    onApproversSelected(approvers: ApprovalPersonaProps[]): void;
    currentSelectedItems?: ApprovalPersonaProps[];
    error?: string;
    hideLabel?: boolean; // Optional property to hide people picker label
    itemLimit?: number; // Optional property to set a item limit to people picker
    enableEmail?: boolean; // Optional property to allow free email input
    setInitialFocus?: boolean; // Optional property to set initial focus on people picker
    innerRef?: React.RefObject<IBasePicker<IPersonaProps>>; // Optional property to pass in a component ref
    placeholderTitle?: string;
    errorMessage?: string;
    label?: string;
    dataSourceType: DataSourceType;
    dataSourceTeamId?: string;
    onBlur?: (evt: React.FocusEvent<HTMLInputElement>) => void;
    required?: boolean;
    enableTeamTag?: boolean;
}

export const TemplatePeoplePickerContainer: React.FC<ITemplatePeoplePickerProps> = (
    props: ITemplatePeoplePickerProps
): JSX.Element => {
    const {
        hideLabel,
        currentSelectedItems,
        error,
        enableEmail,
        onApproversSelected,
        itemLimit,
        label,
        dataSourceType,
        dataSourceTeamId,
        onBlur,
        required,
        enableTeamTag,
    } = props;

    /**
     * A callback to get text from an item. Used to autofill text in the pickers.
     *
     * @param persona - current value of a persona
     */
    const getTextFromItem = (persona: IPersonaProps): string => {
        return persona.text;
    };

    /**
     * A callback to process a selection after the user selects something from the picker.
     * If the callback returns null, the item will not be added to the picker.
     *
     * @param currentPersonas - current personas selected in a list of personas
     */
    const onChanged = (currentPersonas: IPersonaProps[]): void => {
        props.onApproversSelected(currentPersonas);
    };

    /**
     * A callback for what should happen when a person types text into the input.
     * Returns the already selected items so the resolver can filter them out.
     * If used in conjunction with resolveDelay this will only kick off after the delay throttle.
     */
    const onFilterChanged = (filterText: string, currentPersonas: IPersonaProps[]): Promise<IPersonaProps[]> => {
        let getPeopleResultPromise: Promise<IPersonaProps[]> = Promise.resolve([]);
        if (dataSourceType === DataSourceType.TenantWidePeople) {
            getPeopleResultPromise = getPeopleResultsForQueryInPersonalApp([], filterText, currentPersonas);
        } else if (dataSourceType === DataSourceType.TeamWidePeople) {
            getPeopleResultPromise = enableTeamTag
                ? getPeopleResultsandTeamTagResultsForQueryInTeam(filterText, dataSourceTeamId, currentPersonas)
                : getPeopleResultsForQueryInTeam(filterText, dataSourceTeamId, currentPersonas);
        } else if (dataSourceType === DataSourceType.TenantWidePeopleAndGroup) {
            getPeopleResultPromise = getUsersAndGroupsForQuery(filterText, currentPersonas);
        }

        return getPeopleResultPromise;
    };

    /**
     * A function used to validate if raw text entered into the well can be added into the selected items list
     */
    const validateInput = (input: string): ValidationState => {
        return enableEmail ? validateEmail(input) : validateRawText(input);
    };

    /**
     * Takes in the picker input and modifies it in whichever way
     * the caller wants, i.e. parsing entries copied from Outlook (sample
     * input: "Aaron Reid <aaron>").
     *
     * @param input The text entered into the picker.
     */
    const onInputChange = (input: string): string => {
        const outlookRegEx = /<.*>/g;
        const emailAddress = outlookRegEx.exec(input);

        if (emailAddress && emailAddress[0]) {
            return emailAddress[0].substring(1, emailAddress[0].length - 1);
        }

        return input;
    };

    const onRenderItem = (props: IPeoplePickerItemSelectedProps): JSX.Element => {
        const teamTagIcon = getThemedIcon(IconName.teamTagIcon);
        const { isTeamTag, teamId, id: teamTagId } = props.item as ApprovalPersonaProps;
        const newProps = { ...props };
        if (isTeamTag) {
            newProps.item = {
                ...props.item,
                onRenderPersonaCoin: (personaProps?: IPersonaSharedProps): JSX.Element => (
                    <HoverCard
                        type={HoverCardType.plain}
                        plainCardProps={{
                            onRenderPlainCard: (): JSX.Element => <TeamTagCard teamId={teamId} teamTagId={teamTagId} />,
                        }}>
                        <PersonaCoin
                            {...personaProps}
                            initialsColor={PersonaInitialsColor.transparent}
                            onRenderInitials={(): JSX.Element => teamTagIcon}
                        />
                    </HoverCard>
                ),
            };
        }
        return <PeoplePickerItem {...newProps} />;
    };
    return (
        <ApproverPeoplePickerComponent
            hideLabel={hideLabel}
            label={label}
            itemLimit={itemLimit}
            onApproversSelected={onApproversSelected}
            onFilterChanged={onFilterChanged}
            getTextFromItem={getTextFromItem}
            onChanged={onChanged}
            validateInput={validateInput}
            onInputChange={onInputChange}
            error={error}
            currentSelectedItems={currentSelectedItems}
            onBlur={onBlur}
            required={required}
            ariaLabel={label}
            onRenderItem={onRenderItem}
        />
    );
};
